// Site-wide Language & region popup. The overlay shell (#layout-language) ships
// empty in _Layout; the heavy body (tab panels, ~90 locales, ~47 currencies) is
// fetched from /language (Partials/LanguageContent.cshtml, rendered Layout-less)
// the FIRST time the visitor opens the popup, then cached — so that markup never
// loads with the page. Opened by the header + footer globe buttons
// ([data-language-trigger]); initialised once from _Layout.cshtml.ts.
//
// Selections are client-side only: the chosen locale / currency and the translate
// toggle persist in localStorage and re-apply when the popup reopens. No network
// beyond the one content fetch.

const CONTENT_URL = '/language';

export function initLanguageOverlay(): void {
    const overlay = document.querySelector<HTMLElement>('#layout-language');
    if (!overlay) {
        return; // embedded mode drops the chrome → no globe triggers, no overlay
    }

    const scroll = overlay.querySelector<HTMLElement>('[data-language-content]');
    const closeButton = overlay.querySelector<HTMLButtonElement>('.lang-close');
    const LOADING = '<div class="lang-loading"><span class="lang-spinner" aria-hidden="true"></span></div>';
    let loaded = false;
    let opener: HTMLElement | null = null; // the control that opened the popup; refocused on close

    const close = (): void => {
        overlay.classList.remove('open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        // The dialog is now aria-hidden; pull focus back out to the opener so it
        // never rests inside the hidden subtree (and the user keeps their place).
        opener?.focus();
    };

    const open = (): void => {
        opener = document.activeElement as HTMLElement | null;
        overlay.classList.add('open');
        overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden'; // lock the page behind the popup
        closeButton?.focus(); // move focus into the modal dialog
        void loadContent();
    };

    // Fetch the content once, lazily. The guard flips before the await so a rapid
    // second open can't fire a second request; a failed fetch resets it so the
    // next open retries — showing the spinner again rather than the stale error.
    const loadContent = async (): Promise<void> => {
        if (loaded || !scroll) {
            return;
        }
        loaded = true;
        scroll.innerHTML = LOADING;
        try {
            const res = await fetch(CONTENT_URL);
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            scroll.innerHTML = await res.text(); // trusted, same-origin server fragment
            wireContent(scroll);
        } catch {
            loaded = false;
            scroll.innerHTML = '<p class="lang-empty">Unable to load languages. Please try again.</p>';
        }
    };

    document.querySelectorAll<HTMLElement>('[data-language-trigger]').forEach((trigger) =>
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            open();
        }),
    );

    closeButton?.addEventListener('click', close);
    // Backdrop click — only when the press lands on the dimmed backdrop itself,
    // not inside the dialog.
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
            close();
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && overlay.classList.contains('open')) {
            close();
        }
    });
}

// Wire the freshly-injected content: tab switching, the translate switch, and the
// two independent selection groups (region locales, currencies).
function wireContent(root: HTMLElement): void {
    const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>('.lang-tab'));
    const panels = Array.from(root.querySelectorAll<HTMLElement>('.lang-panel'));
    tabs.forEach((tab) =>
        tab.addEventListener('click', () => {
            const key = tab.dataset.panel;
            tabs.forEach((t) => {
                const on = t === tab;
                t.classList.toggle('active', on);
                t.setAttribute('aria-selected', String(on));
            });
            panels.forEach((panel) => {
                const on = panel.dataset.panel === key;
                panel.classList.toggle('active', on);
                panel.hidden = !on;
            });
            // Reset the scroll position so the new tab opens at its top, not wherever
            // the previous tab was scrolled to. `root` is the .lang-scroll container.
            root.scrollTop = 0;
        }),
    );

    wireSwitch(root.querySelector<HTMLButtonElement>('.translate-switch'));
    wireSelectGroup(root, 'locale');
    wireSelectGroup(root, 'currency');
}

// The translate toggle — a role="switch" button whose on/off state persists.
function wireSwitch(button: HTMLButtonElement | null): void {
    if (!button) {
        return;
    }
    const apply = (on: boolean): void => {
        button.classList.toggle('on', on);
        button.setAttribute('aria-checked', String(on));
    };
    apply(localStorage.getItem('translate') === 'on');
    button.addEventListener('click', () => {
        const next = button.getAttribute('aria-checked') !== 'true';
        apply(next);
        localStorage.setItem('translate', next ? 'on' : 'off');
    });
}

// One selection group — every `.locale[data-<attr>]` cell (the region locales, or
// the currencies). Clicking one selects it (the rest clear) and persists the
// choice under that attr's localStorage key; the saved choice re-applies on load.
function wireSelectGroup(root: HTMLElement, attr: 'locale' | 'currency'): void {
    const cells = Array.from(root.querySelectorAll<HTMLButtonElement>(`.locale[data-${attr}]`));
    if (cells.length === 0) {
        return;
    }

    const select = (chosen: HTMLButtonElement): void => {
        cells.forEach((cell) => {
            const on = cell === chosen;
            cell.classList.toggle('is-selected', on);
            cell.setAttribute('aria-current', on ? 'true' : 'false');
        });
    };

    const saved = localStorage.getItem(attr);
    const savedCell = saved ? cells.find((cell) => cell.dataset[attr] === saved) : undefined;
    if (savedCell) {
        select(savedCell);
    }

    cells.forEach((cell) =>
        cell.addEventListener('click', () => {
            select(cell);
            const value = cell.dataset[attr];
            if (value) {
                localStorage.setItem(attr, value);
            }
        }),
    );
}
